// Modernizr
import 'core-libs/modernizr/modernizr';
// Polyfills
import 'core-libs/polyfills/polyfills-netx';
import window from 'global/window';
// Hammerjs (for cross platform gestures)
import Hammer from 'hammerjs';
// need this loaded
import '@netx/core-app/collections/singleton';
import {setGlobal} from '@netx/core-next';
// Create global event dispatcher
import '@netx/core-next/instance/dispatcher';
import '@netx/core-next/instance/localStore';
import {getPrefixedLogger} from '@netx/core-next/instance/logger';
import {appRouter} from '@netx/core-next/instance/router';
import NetxURL from '@netx/core-next/url';
// Load in backbone
import './lib/backbone-netx';
// jQuery overrides/enhancements
import './lib/jquery-netx';
////////////////////////////////////////////
// !Netx global namespace
////////////////////////////////////////////
////////////////////////////////////////////
// App utility libraries
////////////////////////////////////////////
// Underscore overrides/enhancements
import './lib/underscore-netx';

const logger = getPrefixedLogger('bootstrap');

// Netx global namespace

// Setup global namespace
/**
 * @namespace
 * @static
 */
const Netx = window.Netx || {};
Object.assign(Netx, {
	version: '3.16.4.4dfc51b87',
	/**
	 * @namespace
	 * @static
	 */
	custom: {},
	getUserLevel: () => -1,
	getPreference: () => false,
	setPreference: () => true,
	/**
	 * Various DAM-related static properties
	 * @memberOf Netx
	 * @namespace
	 * @static
	 * @property {array} states - A list of states (provided by the DAM)
	 * @property {array} countries - A list of countries (provided by the DAM)
	 * @property {array} attributes - A list of i18n labels for asset properties
	 * @property {array} assetAttributes.ids - Map from label to IDs for asset metadata types
	 * @property {array} assetAttributes.numberTypes - Labels, warnings & validators for the various numeric sub-types
	 * @property {array} assetAttributes.types - Map from IDs to labels for asset metadata types
	 * @property {array} search - Labels and metadata for the various fields of a searchAssetListObjects() RPC call, as used by Netx.Collections.Assets & others
	 * @todo refactor 'attributes' to 'attributeLabels'
	 * @todo assetAttributes.ids and .types are redundant; refactor.
	 * @private
	 */
	properties: {},
	/**
	 * Netx.router is our instance of Backbone.Router
	 * @memberOf Netx
	 * @namespace
	 * @static
	 */
	router: {
		routes: {},
	},
	/**
	 * We create a dummy Netx.user.profile here describing an unauthenticated user;
	 * This will be overwritten with real user data when we authenticate.
	 * @memberOf Netx
	 * @namespace
	 * @static
	 */
	user: {},
	/**
	 * When controllers need a View to have a public name -- i.e. when several listeners need to operate on the same View --
	 * the controller can put that Views in Netx.views; it can be named anything, but by convention it's usually given the
	 * name of the template, which by convention is the View class name lowercased.
	 * For instance, Netx.views.assetDetailView is an instance of Netx.Views.AssetDetailView used by the asset controller.
	 * @memberOf Netx
	 * @namespace
	 * @static
	 */
	views: {},
	/**
	 * Netx.Collections namespace
	 * @memberof Netx
	 * @namespace
	 * @static
	 */
	Collections: {},
	/**
	 * Netx.Models namespace maintains model class definitions
	 * @memberof Netx
	 * @namespace
	 * @static
	 */
	Models: {},
	/**
	 * Netx.Routers namespace
	 * @memberof Netx
	 * @namespace
	 * @static
	 */
	Routers: {},
	/**
	 * Netx.ShareableTypes holds objects describing things that the DAM can share, along with verbiage to describe those things.
	 * @memberof Netx
	 * @namespace
	 * @static
	 */
	ShareableTypes: {},
	URL: NetxURL,
	/**
	 * Netx.Views namespace
	 * @memberof Netx
	 * @namespace
	 * @static
	 */
	Views: {},
});
export default Netx;

// this is dead, leaving in the event of a cannnot access x of undefined
// should not come up... think all references have been removed
window.custom = {};

////////////////////////////////////////////

////////////////////////////////////////////

window.Hammer = Hammer;
////////////////////////////////////////////
// !App utility libraries
////////////////////////////////////////////

////////////////////////////////////////////
// Netx module intialization
////////////////////////////////////////////
/**
 * Modules already initialized indexed by namespace
 * @type {Array<any>}
 */
const initializedModules = [];
/**
 * Modules currently initializing indexed by namespace and storing the promise to the original initialization call for that module
 * @type {object}
 */
const initializingModules = {};

/**
 * Module initialization helper
 * @function
 * @global
 * @param {Array<any>|object} modules Module(s) to initialize
 * @param {object} opts Options
 */
// https://stackoverflow.com/questions/37787123/is-it-possible-to-let-webpacks-system-import-use-ajax-for-progress-events
// One day we could look into extending the webpack functionality with our own plugin to allow for XHR requests of
// all of our bundles - then we could use a `progress` event on it and dispatch refined progress
const initializeModule = function (modules, opts = {}) {
	// Convert to array if not already
	modules = Array.isArray(modules) ? modules : [modules];

	return new Promise((resolve, reject) => {
		var idx = 0,
			newModules = 0,
			routesToAdd = {},
			total = modules.length;

		// Go on to next module (or resolve)
		function next() {
			idx++;
			idx < total ? init() : finished();
		}

		// Initialize module
		function init() {
			var m = modules[idx],
				namespace = m.namespace;

			logger.debug('initializing module >', namespace);

			// Check if already initialized
			if (_.contains(initializedModules, namespace)) {
				logger.debug('module already initialized >', namespace);
				return next();
			}

			// Are we already initializing this module - listen for its completion
			// and then continue on
			if (!_.isUndefined(initializingModules[namespace])) {
				logger.debug('module already initializing >', namespace);
				return initializingModules[namespace].then(next);
			}

			// We are initializing this module now
			dispatcher.trigger(
				'netx:initializing:module',
				namespace,
				(idx / total) * 100,
				opts.deferred,
				opts.loadUrl,
			);

			// Store promise
			initializingModules[namespace] = m.initialize().then(
				function (routes) {
					logger.debug('module initialized >', namespace);
					// If the module was deferred remove its wildcard route now
					if (opts.deferred && appRouter.routes[m.namespace + '(/*anything)']) {
						delete appRouter.routes[m.namespace + '(/*anything)'];
					}
					// Add routes to queue
					routes && _.extend(routesToAdd, routes);
					// Store namespace in modules initialized array
					initializedModules.push(namespace);
					// A new module was initialized
					newModules++;
					// Remove initialize promise from the initializing array
					delete initializingModules[namespace];
					// Dispatche event
					dispatcher.trigger(
						'netx:initialized:module',
						namespace,
						((idx + 1) / total) * 100,
						opts.deferred,
						opts.loadUrl,
					);
					// Next module or resolve
					next();
				},
				(err) => {
					console.error(err);
				},
			);
		}

		// When all modules are initialized
		function finished() {
			// Defer to allow anything hooked on any of the module initialized promises to respond
			_.defer(function () {
				// Only if new modules were intialized
				if (newModules) {
					// Add all new routes
					appRouter.addRoutes(routesToAdd);
					// Load route url when all modules are loaded
					opts.loadUrl && appRouter.ready && Backbone.history.loadUrl();
				}
			});
			resolve(modules);
		}

		// Start
		init();
	});
};

/**
 * @event module:netx#netx:load:module
 * @param {array|object} modules Module object or an array of module objects
 * @param {object} opts Options
 */
dispatcher.on('netx:load:module', function (modules, opts) {
	opts = opts || {};
	initializeModule(
		modules,
		_.extend(
			{
				deferred: false,
				loadUrl: false,
			},
			_.pick(opts, 'deferred', 'loadUrl'),
		),
	).then(
		function () {
			_.isFunction(opts.initialized) && opts.initialized();
		},
		function (err) {
			_.isFunction(opts.error) && opts.error(err);
		},
	);
});

/**
 * @event module:netx#netx:load:deferred:module
 * @param {array|object} modules Module object or an array of module objects
 * @param {object} opts Options
 */
dispatcher.on('netx:load:deferred:module', function (modules, opts) {
	dispatcher.trigger(
		'netx:load:module',
		modules,
		_.extend({deferred: true}, opts),
	);
});
////////////////////////////////////////////
// !Netx module intialization
////////////////////////////////////////////
